(function () {
  'use strict';

  // Shared click-to-expand video behavior (FLIP morph) for any framed video.
  // A framed in-flow embed morphs to a fixed fullscreen takeover and back via a
  // FLIP transform (the only way to smoothly animate between in-flow and
  // position:fixed). Handles a YouTube/Vimeo <iframe> (src gets autoplay
  // appended to start, reset to stop) and a self-hosted <video> (play/pause).
  //
  // Generic, class-agnostic contract (so multiple components can reuse it):
  //   [data-video-root]                     section wrapper (holds backdrop + close)
  //     [data-video-frame]                  the framed embed that morphs
  //       [data-video-embed]                the <iframe> or <video>
  //       [data-video-expand]               transparent catcher that opens it
  //     [data-video-backdrop] (hidden)      blurred scrim behind the takeover
  //     [data-video-close]                  close button
  // The controller toggles data-expanded / data-morphing on the frame; each
  // component's CSS owns what those states look like.

  const DURATION = 450;

  function init() {
    const frames = document.querySelectorAll('[data-video-frame]');
    for (let i = 0; i < frames.length; i++) {
      setup(frames[i]);
    }
  }

  function setup(frame) {
    if (frame.hasAttribute('data-video-ready')) {
      return;
    }
    frame.setAttribute('data-video-ready', '');

    const root = frame.closest('[data-video-root]');
    if (!root) {
      return;
    }

    const elements = {
      embed: frame.querySelector('[data-video-embed]'),
      expand: frame.querySelector('[data-video-expand]'),
      close: root.querySelector('[data-video-close]'),
      backdrop: root.querySelector('[data-video-backdrop]')
    };

    const isVideo = !!elements.embed && elements.embed.tagName === 'VIDEO';

    const state = {
      expanded: false,
      animating: false,
      baseSrc: elements.embed ? elements.embed.getAttribute('src') : ''
    };

    function morph(toExpanded) {
      const first = frame.getBoundingClientRect();

      // Keep the frame's decorations hidden for the whole morph so they never
      // flash at fullscreen scale during the grow/shrink.
      frame.setAttribute('data-morphing', '');

      // Freeze any idle animation and apply the final state so we can measure it.
      frame.style.animation = 'none';
      toggleData(frame, 'expanded', toExpanded);

      const last = frame.getBoundingClientRect();

      // Invert: make it appear where it was, then play to its natural spot.
      const dx = first.left - last.left;
      const dy = first.top - last.top;
      const sx = last.width ? first.width / last.width : 1;
      const sy = last.height ? first.height / last.height : 1;

      frame.style.transition = 'none';
      frame.style.transformOrigin = 'top left';
      frame.style.transform =
        'translate(' + dx + 'px, ' + dy + 'px) scale(' + sx + ', ' + sy + ')';

      frame.getBoundingClientRect(); // force the inverted state to apply

      // Double rAF: a single one sometimes batches the inverted state and the
      // play into one paint, which makes it snap instead of animate.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          frame.style.transition = 'transform ' + DURATION + 'ms ease';
          frame.style.transform = '';
        });
      });

      state.animating = true;
      window.setTimeout(function () {
        state.animating = false;
        frame.style.transition = '';
        frame.style.transform = '';
        frame.style.transformOrigin = '';
        frame.removeAttribute('data-morphing');
        if (!toExpanded) {
          frame.style.animation = ''; // resume any idle animation
        }
      }, DURATION + 40);
    }

    function startPlayback() {
      if (!elements.embed) {
        return;
      }
      if (isVideo) {
        const played = elements.embed.play();
        if (played && typeof played.catch === 'function') {
          played.catch(function () {});
        }
      } else {
        elements.embed.setAttribute('src', autoplaySrc(state.baseSrc));
      }
    }

    function stopPlayback() {
      if (!elements.embed) {
        return;
      }
      if (isVideo) {
        elements.embed.pause();
      } else {
        elements.embed.setAttribute('src', state.baseSrc);
      }
    }

    function expand() {
      if (state.expanded || state.animating) {
        return;
      }
      state.expanded = true;
      if (elements.backdrop) {
        elements.backdrop.hidden = false;
      }
      document.documentElement.style.overflow = 'hidden';
      morph(true);
      // Start playback only once the container has finished growing to full size.
      window.setTimeout(function () {
        if (state.expanded) {
          startPlayback();
        }
      }, DURATION + 60);
    }

    function collapse() {
      if (!state.expanded || state.animating) {
        return;
      }
      state.expanded = false;
      // Stop playback BEFORE the morph so the FLIP shrinks a light, idle player.
      // A live/playing iframe is heavy enough that the browser can skip the
      // inverted-state paint and snap the close instead of animating it.
      stopPlayback();
      morph(false);
      window.setTimeout(function () {
        if (elements.backdrop) {
          elements.backdrop.hidden = true;
        }
        document.documentElement.style.overflow = '';
      }, DURATION);
    }

    if (elements.expand) {
      elements.expand.addEventListener('click', expand);
    }
    if (elements.close) {
      elements.close.addEventListener('click', collapse);
    }
    if (elements.backdrop) {
      elements.backdrop.addEventListener('click', collapse);
    }
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && state.expanded) {
        collapse();
      }
    });
  }

  function toggleData(el, key, force) {
    if (force) {
      el.setAttribute('data-' + key, 'true');
    } else {
      el.removeAttribute('data-' + key);
    }
  }

  function autoplaySrc(src) {
    if (!src) {
      return src;
    }
    const sep = src.indexOf('?') === -1 ? '?' : '&';
    return src + sep + 'autoplay=1&playsinline=1';
  }

  document.addEventListener('DOMContentLoaded', init);
})();
